import React from 'react';

/**
 * App-level error boundary. Catches any render-time exception from the React
 * tree and shows a friendly recovery UI instead of leaving the user stuck on
 * a blank white page. Common causes that this protects against:
 *   - A child component receives a malformed prop (e.g. FilePreview without
 *     `file`) and dereferences undefined.
 *   - A third-party SDK throws synchronously during render.
 *   - A stale cached bundle references state shape that no longer exists.
 *
 * In production we don't ship a stack trace to end users — only a "reload"
 * action and an option to go back to the dashboard. Developers still see
 * the full error in the console.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    if (typeof console !== 'undefined') {
      console.error('[ErrorBoundary] React render error:', error, info?.componentStack);
    }
    // Send to Meta Pixel as a soft signal so we can spot regressions in the
    // production dashboard. Never throws.
    try {
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'ReactRenderError', {
          message: String(error?.message || error || 'unknown'),
        });
      }
    } catch { /* ignore */ }
  }

  reset = () => {
    this.setState({ hasError: false });
  };

  reload = () => {
    try { window.location.reload(); } catch { /* ignore */ }
  };

  goHome = () => {
    try { window.location.href = '/dashboard'; } catch { /* ignore */ }
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-4">
        <div className="max-w-md w-full bg-white rounded-2xl border border-stone-200 p-6 shadow-sm text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 text-[#AB1C24] flex items-center justify-center mx-auto mb-3 text-2xl font-bold">!</div>
          <h2 className="font-heading text-xl font-semibold text-[#292524] mb-1">
            Algo salió mal / Da ist etwas schiefgegangen
          </h2>
          <p className="text-sm text-[#57534E] mb-5">
            La pantalla no se pudo cargar. No te preocupes: tus datos están a salvo.
            Recarga la página o vuelve al inicio.
            <br />
            <span className="text-xs text-stone-400">
              Der Bildschirm konnte nicht geladen werden — deine Daten sind sicher. Lade die Seite neu oder gehe zurück zum Dashboard.
            </span>
          </p>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={this.reload}
              className="bg-[#AB1C24] hover:bg-[#8A151C] text-white px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="error-boundary-reload"
            >
              Recargar / Neu laden
            </button>
            <button
              onClick={this.goHome}
              className="bg-white border border-stone-300 hover:bg-stone-50 text-[#292524] px-4 py-2 rounded-lg text-sm font-semibold"
              data-testid="error-boundary-home"
            >
              Inicio / Start
            </button>
          </div>
        </div>
      </div>
    );
  }
}
